// PRC-049 (boceto S4): tabla compacta de Seguimientos en Control comercial.
// Una línea por fila (~38 px), botones de 26 px, origen abreviado (diseño 33,
// apartado 12) y una acción por fila: «Cerrar» (compra recuperada), «Valorar»
// (precio o tarifa con revisión vencida; preparado para PRC-050/051) o
// «Actualizar» (abiertos).
import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { J } from '../../../pricingJourney/journeyTokens';
import type { MarginCenterFollowUpInventoryV2Item, MarginCenterFollowUpOrigin } from '../../types/followUpInventory';
import { formatDayDate } from './cadenceManagementTexts';
import { recoveredConclusion } from './FollowUpUpdateDialog';

export const ORIGIN_TAG: Record<MarginCenterFollowUpOrigin, { label: string; color: string; border: string }> = {
  cadence_purchase: { label: 'Cadencia', color: '#fcd34d', border: 'rgba(252,211,77,.45)' },
  price_change: { label: 'Precio', color: '#7dd3fc', border: 'rgba(56,189,248,.45)' },
  tariff_update: { label: 'Tarifa', color: '#7dd3fc', border: 'rgba(56,189,248,.45)' },
  manual: { label: 'Manual', color: '#cbd5e1', border: '#334155' },
};

export const ORIGIN_FILTERS: Array<{ id: MarginCenterFollowUpOrigin | 'all'; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'cadence_purchase', label: 'Cadencia de compra' },
  { id: 'price_change', label: 'Cambio de precio' },
  { id: 'tariff_update', label: 'Actualización de tarifas' },
  { id: 'manual', label: 'Manual' },
];

const thSx = { textAlign: 'left', fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#64748b', p: '6px 12px', borderBottom: '1px solid rgba(148,163,184,.16)', whiteSpace: 'nowrap' } as const;
const tdSx = { p: '6px 12px', height: 38, lineHeight: 1.25, borderBottom: '1px solid rgba(148,163,184,.08)', whiteSpace: 'nowrap', fontSize: 13, verticalAlign: 'middle' } as const;
const clipSx = (max: number) => ({ maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) as const;
// Fechas en hora de Madrid (las revisiones vencidas se calculan igual en el servidor).
const madridDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  return parts; // aaaa-mm-dd
};
const btnSx = { height: 26, px: '10px', fontSize: 12, fontWeight: 700, borderRadius: '8px', fontFamily: J.font } as const;

export type FollowUpRowAction = 'open' | 'update' | 'close_recovered' | 'assess';

interface Props {
  items: MarginCenterFollowUpInventoryV2Item[];
  today: string | null;
  onAction: (item: MarginCenterFollowUpInventoryV2Item, action: FollowUpRowAction) => void;
}

const FollowUpsTable: React.FC<Props> = ({ items, today, onAction }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', color: J.text, fontFamily: J.font }}>
      <thead>
        <tr>
          {['Origen', 'Cliente · referencia', 'Situación', 'Próxima acción'].map((h) => <Box component="th" key={h} sx={thSx}>{h}</Box>)}
          <Box component="th" sx={{ ...thSx, textAlign: 'right' }}>Revisión</Box>
          <Box component="th" sx={{ ...thSx, textAlign: 'right' }}>Abierto</Box>
          <Box component="th" sx={thSx} />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const tag = ORIGIN_TAG[item.origin] ?? ORIGIN_TAG.manual;
          const open = item.status !== 'closed';
          const recovered = open && Boolean(item.recovered_on);
          const reviewDay = madridDay(item.next_review_at);
          const reviewLabel = !reviewDay ? '—' : (today && reviewDay === today ? 'Hoy' : formatDayDate(reviewDay));
          const situation = !open ? 'Expediente cerrado' : recovered ? recoveredConclusion(item).replace(/\.$/, '') : (item.opening_reason || '—');
          const reference = item.scope === 'customer' ? 'Cliente completo'
            : `${item.product_id ?? ''}${item.product_label && item.product_label !== item.product_id ? ` · ${item.product_label}` : ''}`;
          const assess = open && item.review_due && (item.origin === 'price_change' || item.origin === 'tariff_update');
          return (
            <Box component="tr" key={item.id} sx={{ '&:hover td': { bgcolor: 'rgba(56,189,248,.04)' } }}>
              <Box component="td" sx={tdSx}>
                <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', borderRadius: '6px', px: '6px', py: '1px', border: `1px solid ${tag.border}`, color: tag.color }}>{tag.label}</Box>
              </Box>
              <Box component="td" sx={tdSx}>
                <ButtonBase onClick={() => onAction(item, 'open')} title="Abrir el expediente" sx={{ ...clipSx(360), display: 'block', textAlign: 'left', fontFamily: J.font, fontSize: 13, color: J.text, '&:hover .nm': { textDecoration: 'underline' } }}>
                  <Box component="span" className="nm" sx={{ fontWeight: 800 }}>{item.customer_label || item.customer_id || item.product_label || '—'}</Box>
                  {reference && <Box component="span" sx={{ ml: 1, fontSize: 12, color: J.muted }}>{reference}</Box>}
                </ButtonBase>
              </Box>
              <Box component="td" sx={tdSx} title={situation}>
                <Box sx={{ ...clipSx(340), color: recovered ? '#34d399' : open ? J.body : J.muted, fontWeight: recovered ? 700 : 400 }}>{situation}</Box>
              </Box>
              <Box component="td" sx={tdSx} title={item.next_action ?? ''}>
                <Box sx={clipSx(240)}>{open ? (item.next_action || '—') : '—'}</Box>
              </Box>
              <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: open && item.review_due ? J.amber : J.body, fontWeight: open && item.review_due ? 700 : 400 }}>{open ? reviewLabel : '—'}</Box>
              <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatDayDate(madridDay(item.created_at))}</Box>
              <Box component="td" sx={{ ...tdSx, textAlign: 'right', width: 1 }}>
                {recovered ? (
                  <ButtonBase onClick={() => onAction(item, 'close_recovered')} sx={{ ...btnSx, bgcolor: J.sky, color: J.onSky }}>Cerrar</ButtonBase>
                ) : assess ? (
                  <ButtonBase onClick={() => onAction(item, 'assess')} sx={{ ...btnSx, bgcolor: J.sky, color: J.onSky }}>Valorar</ButtonBase>
                ) : open ? (
                  <ButtonBase onClick={() => onAction(item, 'update')} sx={{ ...btnSx, border: '1px solid #334155', color: J.text }}>Actualizar</ButtonBase>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  </Box>
);

export default FollowUpsTable;
